import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { User, Building, Euro, Lock, CreditCard, FileText, Save, Mail, Check, X, Download, FileSignature } from 'lucide-react';
import { Button } from '../components/ui/button';

const PartnerProfilPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const partnerId = searchParams.get('partner_id') || localStorage.getItem('partner_id');
  
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [companyData, setCompanyData] = useState({
    company_name: '',
    company_street: '',
    company_zip: '',
    company_city: '',
    company_phone: '',
    billing_vat_id: ''
  });
  
  const [billingData, setBillingData] = useState({
    billing_email: '',
    billing_vat_id: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirm: ''
  });

  useEffect(() => {
    if (!partnerId) {
      navigate('/partner/registrieren');
      return;
    }
    loadProfile();
  }, [partnerId]);

  const loadProfile = async () => {
    try {
      const headers = { 'X-Partner-ID': partnerId };
      
      const profileRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partner/me`, { headers });
      const profileData = await profileRes.json();
      setProfile(profileData);
      
      setCompanyData({
        company_name: profileData.company_name || '',
        company_street: profileData.company_street || '',
        company_zip: profileData.company_zip || '',
        company_city: profileData.company_city || '',
        company_phone: profileData.company_phone || '',
        billing_vat_id: profileData.billing_vat_id || ''
      });
      
      setBillingData({
        billing_email: profileData.billing_email || '',
        billing_vat_id: profileData.billing_vat_id || ''
      });

      const subRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partner/me/subscription`, { headers });
      const subData = await subRes.json();
      setSubscription(subData);
    } catch (error) {
      console.error('Load failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompany = async () => {
    if (!companyData.company_name || !companyData.company_street || !companyData.company_zip || !companyData.company_city) {
      alert('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partner/me/company`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'X-Partner-ID': partnerId
        },
        body: JSON.stringify(companyData)
      });

      if (!response.ok) throw new Error('Speichern fehlgeschlagen');

      alert('Firmendaten gespeichert');
      await loadProfile();
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBilling = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partner/me/billing`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'X-Partner-ID': partnerId
        },
        body: JSON.stringify(billingData)
      });

      if (!response.ok) throw new Error('Speichern fehlgeschlagen');

      alert('Rechnungsdaten gespeichert');
      await loadProfile();
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.current_password || !passwordData.new_password) {
      alert('Bitte alle Felder ausfüllen');
      return;
    }
    
    if (passwordData.new_password.length < 10) {
      alert('Neues Passwort muss mindestens 10 Zeichen lang sein');
      return;
    }
    
    if (passwordData.new_password !== passwordData.new_password_confirm) {
      alert('Passwörter stimmen nicht überein');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partner/me/change-password`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Partner-ID': partnerId
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Passwort ändern fehlgeschlagen');
      }

      alert('Passwort erfolgreich geändert');
      setPasswordData({ current_password: '', new_password: '', new_password_confirm: '' });
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSupport = () => {
    window.location.href = `mailto:support@dein-gutachten-direkt.de?subject=Support Anfrage - Partner ${profile?.email}`;
  };

  const handleCancellation = () => {
    window.location.href = `mailto:support@dein-gutachten-direkt.de?subject=Kündigung anfragen - Partner ${profile?.email}&body=Ich möchte mein Partner-Abo kündigen.`;
  };

  const handleManageSubscription = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          partner_id: partnerId,
          return_url: `${window.location.origin}/portal/profil?partner_id=${partnerId}`
        })
      });

      if (!response.ok) {
        const error = await response.json();
        alert(error.detail || 'Fehler beim Öffnen des Abo-Portals');
        return;
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Portal session error:', error);
      alert('Fehler beim Öffnen des Abo-Portals');
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Profil & Einstellungen</h1>
            <Button onClick={() => navigate(`/portal/dashboard?partner_id=${partnerId}`)} variant="outline" className="border-gray-300">
              Zurück
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 space-y-8">
        {/* 1. Profil-Übersicht */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-brand-green/10 rounded-xl flex items-center justify-center">
              <User className="w-7 h-7 text-brand-green" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Profil-Übersicht</h2>
              <p className="text-sm text-gray-600">Ihre Account-Informationen</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Firmenname</p>
              <p className="font-semibold text-gray-900">{profile?.company_name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Ansprechpartner</p>
              <p className="font-semibold text-gray-900">{profile?.contact_first_name} {profile?.contact_last_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">E-Mail</p>
              <p className="font-semibold text-gray-900">{profile?.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Status</p>
              <div className="flex gap-2 flex-wrap">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${profile?.paid_subscription_active ? 'bg-brand-green/10 text-brand-green' : 'bg-gray-100 text-gray-600'}`}>
                  {profile?.paid_subscription_active ? <Check className="w-3 h-3 inline mr-1" /> : <X className="w-3 h-3 inline mr-1" />}
                  Abo
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${profile?.email_verified ? 'bg-brand-green/10 text-brand-green' : 'bg-gray-100 text-gray-600'}`}>
                  {profile?.email_verified ? <Check className="w-3 h-3 inline mr-1" /> : <X className="w-3 h-3 inline mr-1" />}
                  E-Mail
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${profile?.onboarding_completed ? 'bg-brand-green/10 text-brand-green' : 'bg-gray-100 text-gray-600'}`}>
                  {profile?.onboarding_completed ? <Check className="w-3 h-3 inline mr-1" /> : <X className="w-3 h-3 inline mr-1" />}
                  Onboarding
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <Button onClick={handleSupport} variant="outline" className="border-2 border-gray-300">
              <Mail className="w-4 h-4 mr-2" />
              Support kontaktieren
            </Button>
          </div>
        </div>

        {/* 2. Firmendaten */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-brand-green/10 rounded-xl flex items-center justify-center">
              <Building className="w-7 h-7 text-brand-green" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Firmendaten</h2>
              <p className="text-sm text-gray-600">Ihre Unternehmensinformationen</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-900 mb-2">Firmenname *</label>
              <input
                type="text"
                value={companyData.company_name}
                onChange={(e) => setCompanyData({ ...companyData, company_name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-900 mb-2">Straße *</label>
              <input
                type="text"
                value={companyData.company_street}
                onChange={(e) => setCompanyData({ ...companyData, company_street: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">PLZ *</label>
              <input
                type="text"
                value={companyData.company_zip}
                onChange={(e) => setCompanyData({ ...companyData, company_zip: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Ort *</label>
              <input
                type="text"
                value={companyData.company_city}
                onChange={(e) => setCompanyData({ ...companyData, company_city: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Telefon</label>
              <input
                type="tel"
                value={companyData.company_phone}
                onChange={(e) => setCompanyData({ ...companyData, company_phone: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">USt-ID</label>
              <input
                type="text"
                value={companyData.billing_vat_id}
                onChange={(e) => setCompanyData({ ...companyData, billing_vat_id: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
          </div>

          <div className="mt-6">
            <Button onClick={handleSaveCompany} disabled={saving} className="bg-brand-green hover:bg-brand-green-dark text-white">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Speichern...' : 'Änderungen speichern'}
            </Button>
          </div>
        </div>

        {/* 3. Abrechnung */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-brand-green/10 rounded-xl flex items-center justify-center">
              <Euro className="w-7 h-7 text-brand-green" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Abrechnung</h2>
              <p className="text-sm text-gray-600">Diese Daten werden für Rechnungen verwendet</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Rechnungs-E-Mail</label>
              <input
                type="email"
                value={billingData.billing_email}
                onChange={(e) => setBillingData({ ...billingData, billing_email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">USt-ID</label>
              <input
                type="text"
                value={billingData.billing_vat_id}
                onChange={(e) => setBillingData({ ...billingData, billing_vat_id: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
          </div>

          <div className="mt-6">
            <Button onClick={handleSaveBilling} disabled={saving} className="bg-brand-green hover:bg-brand-green-dark text-white">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Speichern...' : 'Änderungen speichern'}
            </Button>
          </div>
        </div>

        {/* 4. Passwort */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-brand-green/10 rounded-xl flex items-center justify-center">
              <Lock className="w-7 h-7 text-brand-green" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Passwort ändern</h2>
              <p className="text-sm text-gray-600">Mindestens 10 Zeichen</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Aktuelles Passwort *</label>
              <input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Neues Passwort *</label>
              <input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Neues Passwort wiederholen *</label>
              <input
                type="password"
                value={passwordData.new_password_confirm}
                onChange={(e) => setPasswordData({ ...passwordData, new_password_confirm: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
          </div>

          <div className="mt-6">
            <Button onClick={handleChangePassword} disabled={saving} className="bg-brand-green hover:bg-brand-green-dark text-white">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Speichern...' : 'Passwort aktualisieren'}
            </Button>
          </div>
        </div>

        {/* 5. Abo Status */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-brand-green/10 rounded-xl flex items-center justify-center">
              <CreditCard className="w-7 h-7 text-brand-green" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Abo Status</h2>
              <p className="text-sm text-gray-600">Ihr Partner-Abonnement</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                  subscription?.subscription_active ? 'bg-brand-green/10 text-brand-green' : 
                  subscription?.subscription_status === 'unknown' ? 'bg-gray-100 text-gray-600' :
                  'bg-red-100 text-red-700'
                }`}>
                  {subscription?.subscription_status || 'unknown'}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Betrag</p>
                <p className="font-semibold text-gray-900">{subscription?.plan_tier || 'Pro'} Paket</p>
              </div>
            </div>

            {subscription?.subscription_status === 'unknown' && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <p className="text-sm text-gray-700">
                  Abo-Status nicht verfügbar. Bitte kontaktieren Sie den Support.
                </p>
              </div>
            )}

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Abo verwalten:</strong>
              </p>
              <p className="text-sm text-gray-600">
                Hier kannst du Rechnungen herunterladen, Zahlungsmethode ändern oder kündigen.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                💡 Rechnungen findest du im Stripe Portal unter "Invoice history".
              </p>
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={handleManageSubscription} 
                className="bg-brand-green hover:bg-brand-green-dark text-white"
                disabled={!profile?.subscription_stripe_customer_id}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Abo verwalten
              </Button>
              
              <Button onClick={handleCancellation} variant="outline" className="border-2 border-gray-300">
                Support kontaktieren
              </Button>
            </div>
          </div>
        </div>

        {/* 6. Verträge */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-brand-green/10 rounded-xl flex items-center justify-center">
              <FileText className="w-7 h-7 text-brand-green" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Datenschutz & Verträge</h2>
              <p className="text-sm text-gray-600">Akzeptierte Bedingungen</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Partnervereinbarung */}
            <div className="flex items-center justify-between p-4 bg-brand-green/5 border border-brand-green/20 rounded-xl">
              <div className="flex items-center gap-3">
                <FileSignature className="w-5 h-5 text-brand-green" />
                <div>
                  <p className="font-semibold text-gray-900">Partnervereinbarung</p>
                  <p className="text-xs text-gray-600">
                    {profile?.agreement_signed 
                      ? `Unterschrieben: ${new Date(profile.agreement_signed_at).toLocaleDateString('de-DE')}` 
                      : 'Noch nicht unterschrieben'}
                  </p>
                </div>
              </div>
              {profile?.agreement_signed ? (
                <Button
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/partner-agreement/download/${partnerId}`, '_blank')}
                  variant="outline"
                  size="sm"
                  className="border-brand-green text-brand-green hover:bg-brand-green/5"
                  data-testid="download-agreement-btn"
                >
                  <Download className="w-4 h-4 mr-1" />
                  PDF
                </Button>
              ) : (
                <Button
                  onClick={() => navigate(`/partner/vereinbarung?partner_id=${partnerId}`)}
                  size="sm"
                  className="bg-brand-green hover:bg-brand-green-dark text-white"
                  data-testid="sign-agreement-btn"
                >
                  Jetzt unterschreiben
                </Button>
              )}
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-xl">
              <div>
                <p className="font-semibold text-gray-900">Partnerbedingungen {profile?.terms_version}</p>
                <p className="text-xs text-gray-600">
                  Akzeptiert: {profile?.agreements_accepted_at ? new Date(profile.agreements_accepted_at).toLocaleDateString('de-DE') : '-'}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-xl">
              <div>
                <p className="font-semibold text-gray-900">Datenschutz/Vertraulichkeit {profile?.privacy_version}</p>
                <p className="text-xs text-gray-600">
                  Akzeptiert: {profile?.agreements_accepted_at ? new Date(profile.agreements_accepted_at).toLocaleDateString('de-DE') : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerProfilPage;
